import { useEffect, useRef, useState } from 'react';

/**
 * IntersectionObserver-based reveal hook.
 * Returns [ref, isVisible]. Add `reveal` + conditional `in` class.
 *
 * NOTE: The IntersectionObserver is set up exactly once per element (mount).
 * We deliberately do NOT depend on `options` in the effect — a new object
 * literal on every render would re-create the observer and the cleanup
 * would tear it down before its callback fires, leaving `visible=false`
 * forever (especially noticeable in nested leaf components).
 */
const useReveal = (options) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  // Stash options in a ref so the effect can read the latest values without
  // listing them as deps.
  const optsRef = useRef(options);
  optsRef.current = options;

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    // Respect reduced-motion + guard against environments without IO:
    // reveal immediately so content is never left hidden.
    const prefersReduced =
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced || typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return undefined;
    }

    // If the element is already within (or above) the viewport on mount,
    // reveal it right away. IntersectionObserver does not always emit an
    // initial callback for already-intersecting elements in every browser,
    // which previously left above-the-fold content stuck at opacity 0.
    const rect = el.getBoundingClientRect();
    const vh = window.innerHeight || document.documentElement.clientHeight;
    if (rect.top < vh && rect.bottom > 0) {
      setVisible(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.unobserve(el);
        }
      },
      { threshold: 0.12, ...(optsRef.current || {}) },
    );
    observer.observe(el);

    // Safety net: never let content remain invisible if IO fails to fire.
    const fallback = setTimeout(() => setVisible(true), 1500);

    return () => {
      clearTimeout(fallback);
      observer.disconnect();
    };
  }, []);

  return [ref, visible];
};

export default useReveal;
